import { Injectable }   from "@angular/core";
import { InfinityUtil, bindAll } from 'src/infinity-ui'; // { set, store, itemReducer }

@Injectable({ providedIn: "root" })
export class ErrorService {

    constructor(public util: InfinityUtil) { bindAll(this); }

    /* ----- [Root] Server Errors ----- */

    programOpenError() {
        const { supportEmail, programName, programOpenResponse } = this.util.store;
        const enrollmentWindowStartDate = new Date(programOpenResponse.enrollmentWindowStartDate);
        const enrollmentWindowEndDate = new Date(programOpenResponse.enrollmentWindowEndDate);
        const currentDate = new Date();
        const month = enrollmentWindowStartDate.toLocaleDateString('en-US', { month: 'long' });
        const day = enrollmentWindowStartDate.getDate();
        const ordinalDay = `${day}${this.getOrdinalSuffix(day)}`; // e.g.,"3rd", "7th" or "21st"

        let message: string;

        if (currentDate < enrollmentWindowStartDate) {
            this.setError(`
                <h2>Please visit us on ${month} ${ordinalDay} when Enrollment Begins.</h2>
                <p>The Group Personal Excess Liability enrollment site is only available during annual open enrollment period.</p>
                <p>Should you have any questions or requests outside open enrollment,
                please contact <a href='mailto:${supportEmail}'>${supportEmail}</a> for assistance.</p>
            `, 0);
        } else if (currentDate > enrollmentWindowEndDate) {
            this.setError(`
                <h2>Enrollment is Currently Closed.</h2>
                <p>The Group Personal Excess Liability enrollment site is only available during annual open enrollment period.</p>
                <p>Should you have any questions or requests outside open enrollment,
                please contact <a href='mailto:${supportEmail}'>${supportEmail}</a> for assistance.</p>
                `, 0);
        } else {
            this.setError(`
                <h2>Program Unavailable</h2>
                <p>It looks like the program you're attempting to apply for is currently unavailable.</p>
                <p>Please contact <a href='mailto:${supportEmail}'>${supportEmail}</a> for assistance.</p>
                `, 0);
        }
    }

    opsApiStatusError() {
        const { supportEmail } = this.util.store;
        this.setError(`
            <h2>Application Unavailable</h2>
            <p>It looks like the application you're attempting to apply for is currently unavailable.</p>
            <p>Please try again later or contact <a href='mailto:${supportEmail}'>${supportEmail}</a> for assistance.</p>
        `);
    }

    timeoutError() {
        this.setError("<h2>Sorry about the wait.</h2><p>Our backend seems to be taking longer than expected to respond.</p><p>Please wait a few moments or try refreshing if the error persists.</p>", 0);
    }

    serverError(messages?: Array<any>) {
        if (messages instanceof Array && typeof messages[0] !== "string") { messages = messages.map((error: any) => error.errorDescription).filter(e => !!e); }
        if (!messages) { messages = ["The server may be having trouble responding.", "If the error persists please try again later."]; }
        const message = messages.map(m => `<p>${m}</p>`).join("");
        this.setError(`<h2>Backend Error.</h2>${message}`, 0);
    }

    /* ----- [Root] Get Application Form Error -----*/

    applicationFormError(errors: Array<any>) {
        const { programName, supportEmail } = this.util.store;

        // Program not in active enrollment.
        const inactiveEnrollment = errors[0] && errors[0].indexOf("active enrollment") > -1;
        if (inactiveEnrollment) {
            return this.setError(`<h2>We're sorry!</h2>
                <p>We are not currently taking enrollment for <strong>${programName.toUpperCase()}</strong>.</p>
                <p>If you believe that you have received this message in error, please contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.</p>`);
        }

        // Program does not exist.
        const doesNotExist = errors[1] && errors[1].indexOf("does not exist") > -1;
        if (doesNotExist) {
            return this.setError(`<h2>We're sorry!</h2>
                <p>We do not have a program for <strong>${programName.toUpperCase()}</strong>.</p>
                <p>If you believe that you have received this message in error, please contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.</p>`);
        }

        // Do not log server error payloads; they may contain participant data or
        // values originating from an emailed capability link.
    }

    /* ----- [Landing] Verify Renewal Email Errors ----- */

    alreadyRenewed() {
        this.setError(`<h2>Already Renewed</h2>
            <p>It looks like the policy associated with this email has already been renewed.</p>
            <p>If you are applying for new business, please try again with a different email.</p>`, 0);
    }

    renewalTooEarly() {
        const { supportEmail, renewalDays } = this.util.store;

        this.setError(`<h2>Renewal Too Early</h2>
            <p>Hello there!  You have a current and active policy in place.  It is too soon to renew your coverage. ${renewalDays == null ? "" : "As a reminder, a policy can only be renewed " + renewalDays + " days before it expires."}</p
            <p>If you have additional question, please contact <a href="mailto:${supportEmail}">${supportEmail}</a></p>`, 0);
    }

    policyNotYetInEffect() {
        this.setError(`<h2>Policy Not Yet In Effect</h2>`, 0);
    }

    /* ----- [Register] Form Validation Errors ----- */

    captchaError() {
        const { supportEmail } = this.util.store;
        this.setError(`<h2>Recaptcha Error</h2>
            <p>Sorry, it looks like Google thinks you're a robot. If you're not a robot, please try to submit again.</p>
            <p>If you're still unable to submit, please contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.</p>`);
    }

    registerFormInvalid() {
        const requiredItems = this.util.itemReducer([], this.util.store.registerForm).filter(e => e.visible && e.required && !e.valid);
        const firstItemInput: any = document.getElementById(requiredItems[0].itemId);
        if (!firstItemInput) { return; }
        const { type, offsetTop, parentElement } = firstItemInput;
        let scroll = (type === "checkbox" ? parentElement.offsetTop : offsetTop) - (type ? 50 : 25);
        if (requiredItems[0].type === "aggregate") { scroll -= 50; }
        this.setError(`<h2>Incomplete Form</h2><p>Please complete all required fields.</p>`, scroll);
    }

    /* ----- [Payment] Participant Summary Errors ----- */

    participantRatingSummaryError() {
        const { supportEmail, baseUrl } = this.util.store;
        this.setError(`<h2>Quote Unavailable</h2>
            <p>The quote you are selecting is no longer available.</p>
            <p>Please create a new quote or contact your account team for assistance at <a href="mailto:${supportEmail}">${supportEmail}</a>.</p>`);

        this.util.watch("error", e => {
            if (!e.value) { window.location.href = baseUrl; }
        }, { once: true });
    }

    appliedPayInitiateError() {
        const { supportEmail } = this.util.store;
        this.setError(`<h2>Payment Temporarily Unavailable</h2>
            <p>We couldn't complete the payment request. Refresh your session to start a new request.</p>
            <p>If the problem continues, contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.</p>`);
    }

    /* ----- Middleware ----- */

    setError(content: string, scroll?: number) {
        const { error, loading } = this.util.store;
        const updates: any = { error, loading };
        if (scroll !== undefined) { updates.scroll = scroll; }
        error.value = content;
        loading.value = false;
        this.util.set(updates, { batch: true });
    }

    getErrors(key: string, obj: any): Array<any> {
        // Handle missing response. Only null/undefined is "missing" — a valid falsy primitive
        // (e.g. the boolean `false` returned by the isFeatureFlagEnabled endpoint when a flag is off)
        // must NOT be treated as a backend error.
        if (obj === null || obj === undefined) { return [`${key} is null or undefined.`]; }

        // Handle object with errors.
        const errors = obj.errors || [];
        if (errors.length) { return [`${key} contains errors.`].concat(errors.map(e => {
            let description = e.errorDescription;
            if (description && description.indexOf("<!DOCTYPE html>") > -1) {   // If an error contains an entire document: (ex. 403 from microsoft)
                let start = description.indexOf("<style");                      // Find style opening tag.
                let end = description.indexOf("</style>") + "</style>".length;  // Find style closing tag.
                return description.slice(0, start) + description.slice(end);    // Return description without style element.
            }
            return description;
        }).filter(e => !!e)); }

        // Handle inactive enrollment.
        if (obj.activeEnrollment === false) { return [`${key} not in active enrollment.`]; }
        if (key === "programOpenResponse" && !obj.isValid) { return [true]; }

        // Handle inner exceptions.
        const innerException = obj.innerException;
        if (innerException) { return [`${key} inner exception.`].concat(innerException.exceptionMessage || []); }
        
        // Fallthrough return empty error array.
        return [];
    }

    // Function to get the ordinal suffix for a date
    private getOrdinalSuffix(day: number): string {
        if (day > 3 && day < 21) { return 'th'; } // Covers 11th through 19th
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }    
}
